/**
 * Compatible with Pixi WebGL / WebGPU renderers without depending on pixi.js.
 */
export type GpuTextureLimitRenderer = {
  texture?: { maxTextureSize?: number };
  device?: { gpu?: { device?: { limits?: { maxTextureDimension2D?: number } } } };
  gl?: WebGLRenderingContext | WebGL2RenderingContext;
};

const DEFAULT_MAX_GPU_TEXTURE_SIZE = 4096;

/**
 * Resolves the GPU's max 2D texture dimension from a renderer when available.
 * Prefers Pixi texture-system / WebGPU device limits, then WebGL `MAX_TEXTURE_SIZE`.
 */
export function resolveMaxGpuTextureSize(
  renderer: unknown,
  fallback = DEFAULT_MAX_GPU_TEXTURE_SIZE
): number {
  if (!renderer || typeof renderer !== 'object') {
    return fallback;
  }

  const { texture, device, gl } = renderer as GpuTextureLimitRenderer;

  const fromTextureSystem = texture?.maxTextureSize;
  if (typeof fromTextureSystem === 'number' && fromTextureSystem > 0) {
    return fromTextureSystem;
  }

  const fromWebGpu = device?.gpu?.device?.limits?.maxTextureDimension2D;
  if (typeof fromWebGpu === 'number' && fromWebGpu > 0) {
    return fromWebGpu;
  }

  if (gl) {
    const size: unknown = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    if (typeof size === 'number' && size > 0) {
      return size;
    }
  }

  return fallback;
}
